import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { MailIcon, SubmitProofTimerIcon } from '@/components/atoms/icons';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { logger } from '@/core/logger';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { hasTextValue } from '@/core/utils/fieldAvailability';
import React from 'react';
import { Linking, Platform, Pressable, StyleSheet, View } from 'react-native';
import type { UnitDetailsLabels } from '../constants/getPropertyLabels';
import type { CompletePaymentMethod } from '../hooks/useCompletePaymentFlow';
import { usePropertyLabels } from '../hooks/usePropertyLabels';
import { PaymentFlowMobileScreen, useIsPaymentPhoneLayout } from './PaymentFlowMobileScreen';

/** Email chip holds at least this many characters before wrapping. */
const EMAIL_MIN_CHARS = 50;
/**
 * Matter-Regular 16px per-character budget. Measured widths for 50 characters:
 * 334px all digits, 406px a typical address, 464px all uppercase.
 */
const EMAIL_CHAR_WIDTH = 9.4;
const EMAIL_ICON_SIZE = 18;
const EMAIL_CHIP_GAP = 12;
const EMAIL_CHIP_PADDING_X = 24;
const EMAIL_TEXT_WIDTH = Math.ceil(EMAIL_MIN_CHARS * EMAIL_CHAR_WIDTH);
/** Cap only — the chip hugs shorter emails. */
const EMAIL_CHIP_MAX_WIDTH =
  EMAIL_TEXT_WIDTH + EMAIL_CHIP_PADDING_X * 2 + EMAIL_ICON_SIZE + EMAIL_CHIP_GAP;
const CARD_PADDING_X = 16;
/** Figma 425×390 — the card only grows past this for a long email. */
const DIALOG_WIDTH = 425;
const DIALOG_MAX_WIDTH = EMAIL_CHIP_MAX_WIDTH + CARD_PADDING_X * 2;
const DIALOG_MIN_HEIGHT = 390;
const TIMER_BADGE_SIZE = 72;
const TIMER_ICON_SIZE = 32;
/** Please email / chip / Our team */
const COPY_CLUSTER_GAP = 16;
/** Icon ↔ title, title ↔ copy, copy ↔ button */
const SECTION_GAP = 24;
/** Figma Submit Proof yellow */
const PROOF_ACCENT = '#FBC02D';
const TIMER_BADGE_BG = '#FFF9C4';

export interface SubmissionReceivedDialogProps {
  visible: boolean;
  onBackToUnitDetails: () => void;
  method?: CompletePaymentMethod | null;
}

function ProofEmailChip({
  email,
  colors,
  compact,
}: Readonly<{
  email: string;
  colors: ReturnType<typeof useUIColors>;
  compact: boolean;
}>) {
  const openMail = () => {
    Linking.openURL(`mailto:${email}`).catch((error) => {
      logger.error('SubmissionReceivedDialog: mailto failed', error);
    });
  };

  return (
    <Pressable
      onPress={openMail}
      accessibilityRole="link"
      accessibilityLabel={email}
      style={[
        styles.emailChip,
        compact ? null : styles.emailChipDesktop,
        {
          backgroundColor: colors.backgroundLevel1,
          borderLeftColor: PROOF_ACCENT,
        },
      ]}
    >
      <MailIcon size={EMAIL_ICON_SIZE} color={colors.fontSecondary} />
      <Typography
        variant="body"
        color={colors.fontSecondary}
        style={styles.emailText}
      >
        {email}
      </Typography>
    </Pressable>
  );
}

function SubmitProofBody({
  email,
  instruction,
  labels,
  colors,
  compact,
}: Readonly<{
  email: string;
  instruction: string;
  labels: UnitDetailsLabels;
  colors: ReturnType<typeof useUIColors>;
  compact: boolean;
}>) {
  return (
    <View style={styles.copyStack}>
      <Typography
        variant="body"
        color={colors.fontSecondary}
        style={styles.instruction}
      >
        {instruction}
      </Typography>
      {hasTextValue(email) ? (
        <ProofEmailChip email={email} colors={colors} compact={compact} />
      ) : null}
      <Typography
        variant="body"
        color={colors.fontSecondary}
        style={styles.reviewNote}
      >
        {labels.SUBMIT_PROOF_REVIEW_NOTE}
      </Typography>
    </View>
  );
}

function SubmitProofHero({
  title,
}: Readonly<{ title: string }>) {
  return (
    <View style={styles.hero}>
      <View style={styles.timerBadge}>
        <SubmitProofTimerIcon size={TIMER_ICON_SIZE} color={PROOF_ACCENT} />
      </View>
      <Typography variant="title1" color="primary" style={styles.title}>
        {title}
      </Typography>
    </View>
  );
}

/**
 * Submit Your Proof — email the receipt / mortgage docs.
 * Opens after bank transfer confirm and when Proof of Mortgage is chosen.
 */
export function SubmissionReceivedDialog({
  visible,
  onBackToUnitDetails,
  method,
}: Readonly<SubmissionReceivedDialogProps>) {
  const colors = useUIColors();
  React.useEffect(() => {
    if (visible) {
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.TOP_UP,
        actionName: ALMETLAA_ACTION.TOP_UP.PAYMENT_PROOF_RECEIVED_DIALOG_VIEW,
        eventType: 'view',
      });
    }
  }, [visible]);
  const isPhone = useIsPaymentPhoneLayout();
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const email = labels.SUBMIT_PROOF_EMAIL.trim();
  const instruction =
    method === 'proof-of-mortgage'
      ? labels.SUBMIT_PROOF_MORTGAGE_INSTRUCTION
      : labels.SUBMIT_PROOF_PAYMENT_INSTRUCTION;
  const copy = (
    <SubmitProofBody
      email={email}
      instruction={instruction}
      labels={labels}
      colors={colors}
      compact={isPhone}
    />
  );

  if (isPhone) {
    return (
      <PaymentFlowMobileScreen
        visible={visible}
        title={labels.SUBMISSION_RECEIVED_TITLE}
        onBack={onBackToUnitDetails}
        backAccessibilityLabel={labels.BACK}
        footer={
          <Button
            title={labels.BACK_TO_UNIT_DETAILS}
            variant="primary"
            fullWidth
            onPress={onBackToUnitDetails}
            style={styles.mobileCta}
            textStyle={styles.ctaLabel}
          />
        }
      >
        <View style={styles.mobileStack}>
          <SubmitProofHero title={labels.SUBMISSION_RECEIVED_TITLE} />
          {copy}
        </View>
      </PaymentFlowMobileScreen>
    );
  }

  return (
    <ContentDialog
      visible={visible}
      onClose={onBackToUnitDetails}
      contentAlign="center"
      maxWidth={DIALOG_MAX_WIDTH}
      icon={
        <View style={styles.timerBadge}>
          <SubmitProofTimerIcon size={TIMER_ICON_SIZE} color={PROOF_ACCENT} />
        </View>
      }
      title={labels.SUBMISSION_RECEIVED_TITLE}
      titleVariant="title1"
      titleColor="primary"
      titleStyle={styles.title}
      headerStyle={styles.header}
      cardStyle={[
        styles.card,
        styles.cardDesktop,
        { backgroundColor: colors.backgroundLevel2 },
      ]}
      actionsStyle={styles.actions}
      closeOnBackdropPress
      actions={[
        {
          label: labels.BACK_TO_UNIT_DETAILS,
          variant: 'primary',
          flex: false,
          fullWidth: false,
          onPress: onBackToUnitDetails,
          style: styles.ctaButton,
          textStyle: styles.ctaLabel,
        },
      ]}
    >
      {copy}
    </ContentDialog>
  );
}

const styles = StyleSheet.create({
  /** Figma: 425×390, radius 16, pad 32/16. Section gap 24. */
  card: {
    width: '100%',
    borderRadius: 16,
    paddingTop: 32,
    paddingRight: CARD_PADDING_X,
    paddingBottom: 32,
    paddingLeft: CARD_PADDING_X,
    gap: SECTION_GAP,
    alignItems: 'center',
    overflow: 'visible',
    ...(Platform.OS === 'web'
      ? ({ boxSizing: 'border-box' } as object)
      : null),
  },
  /** Figma size floor; grows to the cap only when a long email needs it. */
  cardDesktop: {
    width: 'auto',
    minWidth: DIALOG_WIDTH,
    maxWidth: DIALOG_MAX_WIDTH,
    minHeight: DIALOG_MIN_HEIGHT,
    alignSelf: 'center',
    flexShrink: 0,
  },
  header: {
    gap: SECTION_GAP,
    alignItems: 'center',
    width: '100%',
  },
  timerBadge: {
    width: TIMER_BADGE_SIZE,
    height: TIMER_BADGE_SIZE,
    borderRadius: TIMER_BADGE_SIZE / 2,
    borderWidth: 1,
    borderColor: PROOF_ACCENT,
    backgroundColor: TIMER_BADGE_BG,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /** Figma Font/Size/2XL Medium 28, LH 100% */
  title: {
    fontFamily: 'Matter-Medium',
    fontWeight: Platform.OS === 'web' ? '400' : '500',
    fontSize: 28,
    lineHeight: 28,
    textAlign: 'center',
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
  /** Figma Font/Size/Base Regular 16, LH 22, #4D4D4D */
  instruction: {
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 22,
    textAlign: 'center',
  },
  copyStack: {
    width: '100%',
    alignItems: 'center',
    gap: COPY_CLUSTER_GAP,
  },
  /** Hugs the email; `alignSelf` keeps it from stretching to the stack width. */
  emailChip: {
    maxWidth: '100%',
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    alignSelf: 'center',
    gap: EMAIL_CHIP_GAP,
    borderRadius: 8,
    borderLeftWidth: 4,
    paddingTop: 8,
    paddingRight: EMAIL_CHIP_PADDING_X,
    paddingBottom: 8,
    paddingLeft: EMAIL_CHIP_PADDING_X,
    ...(Platform.OS === 'web'
      ? ({ boxSizing: 'border-box' } as object)
      : null),
  },
  /** Desktop cap: 50 characters, then the email wraps. */
  emailChipDesktop: {
    maxWidth: EMAIL_CHIP_MAX_WIDTH,
  },
  /** Wraps onto a second line instead of clipping past the chip. */
  emailText: {
    flexShrink: 1,
    minWidth: 0,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 22,
    textAlign: 'left',
    ...(Platform.OS === 'web'
      ? ({ overflowWrap: 'anywhere', wordBreak: 'break-word' } as object)
      : null),
  },
  reviewNote: {
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 22,
    textAlign: 'center',
  },
  actions: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  /** Figma: 220×48, gap 4, radius Spacing/1 (4), padding Spacing/3 (12) */
  ctaButton: {
    width: 220,
    height: 48,
    minWidth: 220,
    gap: 4,
    borderRadius: 4,
    paddingTop: 12,
    paddingRight: 12,
    paddingBottom: 12,
    paddingLeft: 12,
    alignSelf: 'center',
  },
  hero: {
    width: '100%',
    alignItems: 'center',
    gap: SECTION_GAP,
  },
  mobileStack: {
    width: '100%',
    alignItems: 'center',
    gap: SECTION_GAP,
  },
  mobileCta: {
    width: '100%',
    height: 48,
    borderRadius: 4,
  },
  /** Figma Font/Size/Base SemiBold 16, LH 100%, center */
  ctaLabel: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
});

export default SubmissionReceivedDialog;
